import { scaleTime } from 'd3-scale';
import { timeFormatDefaultLocale } from 'd3-time-format';
import { scaleDiscontinuous } from '@d3fc/d3fc-discontinuous-scale';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useTheme } from 'next-themes';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Point,
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { D3_LOCALES } from '@/localization/d3-time-format';
import useLocale from '@/hooks/useLocale';

const LIGHT_LINE_COLOR = 'rgb(4, 79, 137)';

const DARK_LINE_COLOR = 'rgb(0, 160, 224)';

export function LineChart({ filteredEntries, mode, question }) {
  const { t } = useTranslation();
  const { patientLocale: locale } = useLocale();

  const { theme } = useTheme();

  const LINE_COLOR = theme === 'light' ? LIGHT_LINE_COLOR : DARK_LINE_COLOR;

  useEffect(() => {
    if (locale && _.has(D3_LOCALES, locale)) {
      timeFormatDefaultLocale(D3_LOCALES[locale]);
    }
  }, [locale]);

  const discontinuousScale = scaleDiscontinuous(scaleTime());

  const entries = filteredEntries ?? question.patientEntries;

  const data = entries.map(({ answer, date, skipped }) => {
    const answerValue = _.toNumber(answer);
    // exclude entries that cannot be parsed to a number
    if (!_.isNil(answer) && _.isNumber(answerValue)) {
      return {
        answer: answerValue,
        // Victory requires the date to be a JS date object
        date: new Date(date),
        skipped,
      };
    }
  });

  const areaData = [...data];
  _.remove(areaData, ['skipped', true]);

  const dependentAxis = () => {
    let yValues;
    let yLabels;

    const { answers, stepValue, type, yMin, yMax } = question;

    // for numerical input, set the min and max for y-scale based on patient entries +/- step value
    if (type === 'numerical_input') {
      const max = yMax || _.maxBy(data, 'answer').answer + stepValue;
      let min = yMin || _.minBy(data, 'answer').answer - stepValue;
      min = min < 0 ? 0 : min;
      yValues = [min, max];

      if (stepValue >= 2) {
        yValues = _.range(min, max, stepValue).concat(max);
      }

      yLabels = [...yValues];

      return { yValues, yLabels };
    }
    // for scale and picker question types
    // convert answer keys to their numerical values through index positions
    yValues = answers.map((_value, index) => index);

    yLabels = answers.map(({ chartLabel }) => formatChartLabel(chartLabel));

    return { yValues, yLabels };
  };

  // long labels will get cut off, so shorten and break up the text as much as possible
  // https://formidable.com/open-source/victory/docs/faq/#my-axis-labels-are-cut-off-how-can-i-fix-them
  const formatChartLabel = (string) =>
    string.replace(' degrees', '°').trim().split(' ').join('\n');

  const { yValues, yLabels } = dependentAxis();

  const answerLabel = (datum) => {
    let date = dayjs(datum.date).format('LL');

    if (mode === 'day') {
      date = dayjs(datum.date).format('ha');
    }

    if (question.type === 'numerical_input') {
      return `${date}\n ${datum.answer} ${question.unit}`;
    }

    return `${date}\n ${yLabels[datum.answer]} ${question.unit}`;
  };

  const independentAxis = useMemo(() => {
    const defaultProps = {
      tickValues: null,
      tickFormat: null,
    };

    switch (mode) {
      case 'day': {
        const startOfDay = dayjs(data[0].date).startOf('day');
        const tickValues = [startOfDay];

        for (let i = 0; i <= 2; i += 1) {
          const hour = dayjs(tickValues[i]).add(6, 'hours');
          tickValues.push(new Date(hour));
        }

        return {
          tickValues,
          tickFormat: (datum, i) => {
            if (i === 0) return `${dayjs(datum).format('MMM D')}`;
            return `${dayjs(datum).format('ha')}`;
          },
        };
      }

      case 'week': {
        const startOfWeek = dayjs(data[0].date).startOf('isoWeek');
        const tickValues = [];

        for (let i = 0; i <= 6; i += 1) {
          const dayOfWeek = dayjs(startOfWeek).add(i, 'days');
          tickValues.push(new Date(dayOfWeek));
        }

        return {
          tickValues,
          tickFormat: (datum) => `${dayjs(datum).format('ddd')}`,
        };
      }

      case 'all':
      default:
        return defaultProps;
    }
  }, [data, mode]);

  const { tickFormat, tickValues } = independentAxis;
  const safetyMin = _.first(question.answers).safetyMin;
  const safetyMax = _.first(question.answers).safetyMax;

  const scatterDotSize = ({ datum }) => {
    if (datum.skipped) return 0;

    const dangerZone =
      (safetyMax && datum.answer > safetyMax) || datum.answer < safetyMin;

    if (dangerZone) {
      return 4;
    }
    return 2;
  };

  const CirclePoint = (props) => {
    const { x, y, active } = props;

    if (active)
      return (
        <g>
          <line
            x1={x}
            y1={0}
            x2={x}
            y2={500}
            stroke="#777777"
            stroke-dasharray={3}
          />
          <circle cx={x} cy={y} r="3" fill="#ffffff" />
        </g>
      );

    return;
  };

  return (
    <div>
      <VictoryChart
        data={data}
        x="date"
        y="answer"
        scale={{ x: discontinuousScale }}
        minDomain={{ y: _.first(yValues) }}
        maxDomain={{ y: _.last(yValues) }}
        containerComponent={
          <VictoryVoronoiContainer
            role="presentation"
            voronoiBlacklist={['area', 'scatter']}
            voronoiDimension="x"
            labels={({ datum }) => (datum.skipped ? null : answerLabel(datum))}
            labelComponent={
              <VictoryTooltip
                y0={-100}
                flyoutWidth={120}
                flyoutHeight={60}
                pointerWidth={18}
                cornerRadius={0}
                constrainToVisibleArea
                flyoutStyle={{
                  fill: theme === 'light' ? 'black' : 'white',
                }}
                style={{
                  fill: theme === 'light' ? 'white' : 'black',
                }}
              />
            }
          />
        }
        width={600}
        height={400}
      >
        {!_.isEmpty(tickValues) ? (
          <VictoryAxis
            fixLabelOverlap
            tickValues={tickValues}
            tickFormat={tickFormat}
            style={{
              axis: { stroke: 'grey' },
              tickLabels: { fill: 'grey', fontSize: 18 },
            }}
          />
        ) : (
          <VictoryAxis
            fixLabelOverlap
            tickFormat={tickFormat}
            style={{
              axis: { stroke: 'grey' },
              tickLabels: { fill: 'grey', fontSize: 18 },
            }}
          />
        )}
        <VictoryAxis
          dependentAxis
          invertAxis={question.reverseY}
          tickValues={yValues}
          tickFormat={yLabels}
          style={{
            axis: { stroke: 'grey' },
            axisLabel: { fontSize: 18 },
            grid: { stroke: 'grey' },
            tickLabels: { fill: 'grey', fontSize: 18 },
          }}
        />
        {areaData.length > 1 && (
          <VictoryLine
            scale="time"
            style={{
              data: {
                stroke: '#044F89',
                strokeWidth: 3,
              },
            }}
            x="date"
            y="answer"
            data={areaData}
            name="area"
            y0={() => (question.reverseY ? _.last(yValues) : _.first(yValues))}
          />
        )}
        <VictoryScatter
          data={data}
          dataComponent={<CirclePoint />}
          x="date"
          y="answer"
          size={scatterDotSize}
        />
        <VictoryScatter
          name="scatter"
          data={data}
          dataComponent={
            <Point
              tabIndex={0}
              ariaLabel={({ datum }) =>
                datum.skipped
                  ? `${datum.date}, ${t('linechart.label.blank')}}`
                  : `${datum.date}, ${datum.answer}`
              }
            />
          }
          x="date"
          y="answer"
          size={scatterDotSize}
          style={{
            data: {
              fill: ({ datum }) =>
                (safetyMax && datum.answer > safetyMax) ||
                datum.answer < safetyMin
                  ? 'red'
                  : LINE_COLOR,
              stroke: ({ datum }) =>
                (safetyMax && datum.answer > safetyMax) ||
                datum.answer < safetyMin
                  ? 'red'
                  : LINE_COLOR,
              strokeWidth: 2,
            },
          }}
        />
      </VictoryChart>
    </div>
  );
}
